import { useState, useEffect } from 'react'
import { EducatorsService } from '@/services/educators'
import { Educator, NewEducator } from '@/types/admin'
import { EducatorsTable } from '@/components/admin/educators/EducatorsTable'
import { EducatorDialog } from '@/components/admin/educators/EducatorDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlusCircle, Search, Filter, MapPin, Briefcase } from 'lucide-react'
import { toast } from 'sonner'

export default function EducatorsPage() {
  const [educators, setEducators] = useState<Educator[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingEducator, setEditingEducator] = useState<Educator | null>(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCity, setFilterCity] = useState('')
  const [filterPosition, setFilterPosition] = useState('')
  const [filterAvailability, setFilterAvailability] = useState('all')

  const fetchEducators = async () => {
    try {
      const data = await EducatorsService.getAll()
      setEducators(data)
    } catch (error) {
      toast.error('Erro ao carregar educadores')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEducators()
  }, [])

  const handleCreate = () => {
    setEditingEducator(null)
    setDialogOpen(true)
  }

  const handleEdit = (educator: Educator) => {
    setEditingEducator(educator)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: NewEducator) => {
    try {
      if (editingEducator) {
        await EducatorsService.update(editingEducator.id, data)
        toast.success('Educador atualizado')
      } else {
        await EducatorsService.create(data)
        toast.success('Educador criado')
      }
      fetchEducators()
    } catch (error) {
      toast.error('Erro ao salvar educador')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza?')) return
    try {
      await EducatorsService.delete(id)
      toast.success('Educador excluído')
      fetchEducators()
    } catch (error) {
      toast.error('Erro ao excluir')
    }
  }

  // Filtering Logic
  const filteredEducators = educators.filter((edu) => {
    const matchesSearch =
      edu.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      edu.email.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCity =
      !filterCity || edu.city.toLowerCase().includes(filterCity.toLowerCase())

    const matchesPosition =
      !filterPosition ||
      edu.desired_position.toLowerCase().includes(filterPosition.toLowerCase())

    const matchesAvailability =
      filterAvailability === 'all' ||
      (filterAvailability === 'yes' && edu.immediate_availability) ||
      (filterAvailability === 'no' && !edu.immediate_availability)

    return (
      matchesSearch && matchesCity && matchesPosition && matchesAvailability
    )
  })

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">
            Gerenciar Educadores
          </h1>
          <p className="text-slate-500 mt-1">
            Banco de talentos e profissionais cadastrados.
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-brand-emerald hover:bg-emerald-600"
        >
          <PlusCircle className="mr-2 h-4 w-4" /> Novo Educador
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por nome ou email..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Filtrar por Cidade"
            className="pl-9"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
          />
        </div>

        <div className="relative">
          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Filtrar por Cargo"
            className="pl-9"
            value={filterPosition}
            onChange={(e) => setFilterPosition(e.target.value)}
          />
        </div>

        <div>
          <Select
            value={filterAvailability}
            onValueChange={setFilterAvailability}
          >
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <SelectValue placeholder="Disponibilidade" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Disponibilidades</SelectItem>
              <SelectItem value="yes">Imediata</SelectItem>
              <SelectItem value="no">Aviso Prévio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-emerald mx-auto mb-4"></div>
          <p className="text-slate-500">Carregando educadores...</p>
        </div>
      ) : (
        <EducatorsTable
          educators={filteredEducators}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <EducatorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        educator={editingEducator}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
